import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import * as React from 'react';
import { TextMorph } from '@/components/ui/animated-text';
import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type DynamicButtonState = {
  label: string;
  icon: LucideIcon;
  /** Whether the icon should spin continuously */
  iconSpin?: boolean;
  /** Additional class for the icon (e.g. hover effects) */
  iconClassName?: string;
};

type DynamicButtonProps<S extends Record<string, DynamicButtonState>> = Omit<
  ButtonProps,
  'children'
> & {
  /** The current state key — changing this triggers the animation. */
  current: keyof S & string;
  /** Map of state keys to their visual configuration. */
  states: S;
};

const SpinningIcon = ({
  icon: Icon,
  className,
  reduceMotion
}: {
  icon: LucideIcon;
  className?: string;
  reduceMotion: boolean;
}) => {
  if (reduceMotion) {
    return (
      <span className="inline-flex">
        <Icon className={cn('h-4 w-4', className)} />
      </span>
    );
  }
  return (
    <motion.span
      animate={{ rotate: 360 }}
      className="inline-flex"
      transition={{
        repeat: Number.POSITIVE_INFINITY,
        ease: 'linear',
        duration: 1
      }}
    >
      <Icon className={cn('h-4 w-4', className)} />
    </motion.span>
  );
};

const DynamicButtonInner = <S extends Record<string, DynamicButtonState>>(
  { current, states, className, ...buttonProps }: DynamicButtonProps<S>,
  ref: React.ForwardedRef<HTMLButtonElement>
) => {
  const reduceMotion = useReducedMotion() ?? false;
  const state = states[current];
  if (!state) {
    if (process.env.NODE_ENV !== 'production') {
      // Loud warning rather than silent disappearance — this is almost
      // always a typo in `current` or a missing key in `states`.
      console.warn(
        `DynamicButton: no state found for key "${current}". Available: ${Object.keys(
          states
        ).join(', ')}`
      );
    }
    return null;
  }

  const Icon = state.icon;
  const transition = reduceMotion ? { duration: 0 } : { duration: 0.15 };

  return (
    <Button
      className={cn('group relative', className)}
      ref={ref}
      {...buttonProps}
    >
      <span className="inline-flex items-center">
        <AnimatePresence mode="popLayout">
          <motion.span
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            className="mr-2 inline-flex"
            exit={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.8, filter: 'blur(2px)' }
            }
            initial={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.8, filter: 'blur(2px)' }
            }
            key={current}
            transition={transition}
          >
            {state.iconSpin ? (
              <SpinningIcon
                className={state.iconClassName}
                icon={Icon}
                reduceMotion={reduceMotion}
              />
            ) : (
              <Icon
                className={cn(
                  'h-4 w-4 transition-transform duration-300',
                  state.iconClassName
                )}
              />
            )}
          </motion.span>
        </AnimatePresence>
        <TextMorph as="span" className="inline-flex">
          {state.label}
        </TextMorph>
      </span>
    </Button>
  );
};

const DynamicButtonForwarded = React.forwardRef(DynamicButtonInner);
DynamicButtonForwarded.displayName = 'DynamicButton';

type DynamicButtonComponent = (<S extends Record<string, DynamicButtonState>>(
  props: DynamicButtonProps<S> & React.RefAttributes<HTMLButtonElement>
) => React.ReactElement | null) & { displayName?: string };

export const DynamicButton =
  DynamicButtonForwarded as unknown as DynamicButtonComponent;
